import './custom-devtools-formatter.mjs.js';
import { getFilteredGlobalOwnKeys, createBlueConnector, VirtualEnvironment, createRedConnector, linkIntrinsics, assignFilteredGlobalDescriptorsFromPropertyDescriptorMap } from '@locker/near-membrane-base';
import { toSafeWeakMap, ReflectGetPrototypeOf, ReflectOwnKeys, SetCtor, ReflectApply, SetProtoHas, ReflectDeleteProperty, toSafeWeakSet, WeakSetCtor, WeakMapCtor, TypeErrorCtor, ObjectAssign, isObject } from '@locker/near-membrane-shared';
import { rootWindow, IS_CHROMIUM_BROWSER, HTMLIFrameElementProtoContentWindowGetter, DocumentProtoOpen, DocumentProtoClose, IS_OLD_CHROMIUM_BROWSER, ElementProtoRemove, DocumentProtoCreateElement, DocumentProtoBodyGetter, NodeProtoLastChildGetter, HTMLElementProtoStyleGetter, ElementProtoSetAttribute, NodeProtoAppendChild } from '@locker/near-membrane-shared-dom';
const blueDocumentToRecordMap = toSafeWeakMap(new WeakMap());
// Chromium based browsers have a bug that nulls the result of `window`
// getters in detached iframes when the property descriptor of `window.window`
// is retrieved.
// https://bugs.chromium.org/p/chromium/issues/detail?id=1305302
const unforgeablePoisonedWindowKeys = IS_CHROMIUM_BROWSER ? ['window'] : undefined;
function getCachedGlobalObjectReferences(globalObject) {
  const {
    window
  } = globalObject;
  let record;
  let document;
  // Suppress errors thrown on cross-origin opaque windows.
  try {
    ({
      document
    } = globalObject);
    record = blueDocumentToRecordMap.get(document);
    // eslint-disable-next-line no-empty
  } catch (_unused) {
    return undefined;
  }
  if (record) {
    return record;
  }
  // Cache references to object values that can't be replaced
  // window -> Window -> WindowProperties -> EventTarget
  const WindowProto = ReflectGetPrototypeOf(window);
  const WindowPropertiesProto = ReflectGetPrototypeOf(WindowProto);
  const EventTargetProto = ReflectGetPrototypeOf(WindowPropertiesProto);
  record = {
    document,
    DocumentProto: ReflectGetPrototypeOf(document),
    window,
    WindowProto: ReflectGetPrototypeOf(window),
    WindowPropertiesProto: ReflectGetPrototypeOf(WindowProto),
    EventTargetProto,
    // Some simulated browser environments, e.g. those using JSDOM, may lack an EventTargetProto.
    EventTargetProtoOwnKeys: EventTargetProto ? ReflectOwnKeys(EventTargetProto) : []
  };
  blueDocumentToRecordMap.set(document, record);
  return record;
}
function filterWindowKeys(keys) {
  const excludedKeys = new SetCtor(['document', 'location', 'top', 'window']);
  const result = [];
  let resultOffset = 0;
  for (let i = 0, {
      length
    } = keys; i < length; i += 1) {
    const key = keys[i];
    if (ReflectApply(SetProtoHas, excludedKeys, [key])) {
      continue;
    }
    result[resultOffset++] = key;
  }
  return result;
}
/**
 * global descriptors are a combination of 3 set of descriptors:
 * - first, the key of the red descriptors define the descriptors
 *   provided by the browser when creating a brand new window.
 * - second, once we know the base keys, we get the actual descriptors
 *   from the blueDescriptors, since those are the one we want to provide
 *   access to via the membrane.
 * - third, the user of this library can provide endowments, which define
 *   global descriptors that should be installed into the sandbox on top
 *   of the base descriptors.
 *
 * Note: The main reason for using redDescriptors as the base keys instead
 * of blueDescriptor is because there is no guarantee that this library is
 * the first one to be evaluated in the host app, which means it has no ways
 * to determine what is a real DOM API vs app specific globals.
 *
 * Quirk: The only quirk here is for the case in which this library runs
 * after some other code that patches some of the DOM APIs. This means
 * the installed proxy in the sandbox will point to the patched global
 * API in the blue realm, rather than the original, because we don't have
 * a way to access the original anymore. This should not be a deal-breaker
 * if the patched API behaves according to the spec.
 *
 * The result of this method is a descriptor map that contains everything
 * that will be installed (via the membrane) as global descriptors in
 * the red realm.
 */
function removeWindowDescriptors(unsafeDescs) {
  // Remove unforgeable descriptors that cannot be installed.
  ReflectDeleteProperty(unsafeDescs, 'document');
  ReflectDeleteProperty(unsafeDescs, 'location');
  ReflectDeleteProperty(unsafeDescs, 'top');
  ReflectDeleteProperty(unsafeDescs, 'window');
  // Remove other browser specific unforgeables.
  ReflectDeleteProperty(unsafeDescs, 'chrome');
  return unsafeDescs;
}
/**
 * Initialization operation to capture and cache all unforgeable references
 * and their respective descriptor maps before any other code runs, this
 * usually help because this library runs before anything else that can poison
 * the environment.
 */
getCachedGlobalObjectReferences(rootWindow);
const IFRAME_SANDBOX_ATTRIBUTE_VALUE = 'allow-same-origin allow-scripts';
const revoked = toSafeWeakSet(new WeakSetCtor());
const blueCreateHooksCallbackCache = toSafeWeakMap(new WeakMapCtor());
function createDetachableIframe(doc) {
  var _ReflectApply;
  const iframe = ReflectApply(DocumentProtoCreateElement, doc, ['iframe']);
  // It is impossible to test whether the NodeProtoLastChildGetter branch is
  // reached in a normal Karma test environment.
  const parent = (_ReflectApply = ReflectApply(DocumentProtoBodyGetter, doc, [])) != null ? _ReflectApply : /* istanbul ignore next */ReflectApply(NodeProtoLastChildGetter, doc, []);
  const style = ReflectApply(HTMLElementProtoStyleGetter, iframe, []);
  style.display = 'none';
  ReflectApply(ElementProtoSetAttribute, iframe, ['sandbox', IFRAME_SANDBOX_ATTRIBUTE_VALUE]);
  ReflectApply(NodeProtoAppendChild, parent, [iframe]);
  return iframe;
}
function createIframeVirtualEnvironment(globalObject, providedOptions) {
  if (typeof globalObject !== 'object' || globalObject === null) {
    throw new TypeErrorCtor('Missing global object virtualization target.');
  }
  const blueRefs = getCachedGlobalObjectReferences(globalObject);
  if (typeof blueRefs !== 'object' || blueRefs === null) {
    throw new TypeErrorCtor('Invalid virtualization target.');
  }
  const {
    distortionCallback,
    defaultPolicy,
    endowments,
    globalObjectShape,
    instrumentation,
    keepAlive = true,
    liveTargetCallback,
    maxPerfMode = false,
    signSourceCallback
    // eslint-disable-next-line prefer-object-spread
  } = ObjectAssign({
    __proto__: null
  }, providedOptions);
  const iframe = createDetachableIframe(blueRefs.document);
  const redWindow = ReflectApply(HTMLIFrameElementProtoContentWindowGetter, iframe, []);
  const shouldUseDefaultGlobalOwnKeys = typeof globalObjectShape !== 'object' || globalObjectShape === null;
  const defaultGlobalOwnKeys = filterWindowKeys(getFilteredGlobalOwnKeys(redWindow, maxPerfMode));
  let blueConnector = blueCreateHooksCallbackCache.get(blueRefs.document);
  if (blueConnector === undefined) {
    blueConnector = createBlueConnector(globalObject);
    blueCreateHooksCallbackCache.set(blueRefs.document, blueConnector);
  }
  // Install default TrustedTypes policy in the virtual environment.
  // @ts-ignore trustedTypes does not exist on GlobalObject
  if (typeof redWindow.trustedTypes !== 'undefined' && isObject(defaultPolicy)) {
    // @ts-ignore trustedTypes does not exist on GlobalObject
    redWindow.trustedTypes.createPolicy('default', defaultPolicy);
  }
  const {
    eval: redIndirectEval
  } = redWindow;
  const env = new VirtualEnvironment({
    blueConnector,
    redConnector: createRedConnector(signSourceCallback ? sourceText => redIndirectEval(signSourceCallback(sourceText)) : redIndirectEval),
    distortionCallback,
    instrumentation,
    liveTargetCallback,
    revokedProxyCallback: keepAlive ? revokedProxyCallback : undefined,
    signSourceCallback
  });
  linkIntrinsics(env, globalObject);
  // window
  // window.document
  // In browsers globalThis is === window.
  if (typeof globalThis === 'undefined') {
    // Support for globalThis was added in Chrome 71.
    // However, environments like Android emulators are running Chrome 69.
    env.link('window', 'document');
  } else {
    // document is === window.document.
    env.link('document');
  }
  // window.__proto__ (aka Window.prototype)
  // window.__proto__.__proto__ (aka WindowProperties.prototype)
  // window.__proto__.__proto__.__proto__ (aka EventTarget.prototype)
  env.link('__proto__', '__proto__', '__proto__');
  env.remapProto(blueRefs.document, blueRefs.DocumentProto);
  env.lazyRemapProperties(blueRefs.window, shouldUseDefaultGlobalOwnKeys ? defaultGlobalOwnKeys : filterWindowKeys(getFilteredGlobalOwnKeys(globalObjectShape, maxPerfMode)),
  // Chromium based browsers have a bug that nulls the result of `window`
  // getters in detached iframes when the property descriptor of `window.window`
  // is retrieved.
  // https://bugs.chromium.org/p/chromium/issues/detail?id=1305302
  keepAlive ? undefined : unforgeablePoisonedWindowKeys);
  if (endowments) {
    const filteredEndowments = {};
    assignFilteredGlobalDescriptorsFromPropertyDescriptorMap(filteredEndowments, endowments, maxPerfMode);
    removeWindowDescriptors(filteredEndowments);
    env.remapProperties(blueRefs.window, filteredEndowments);
  }
  // We intentionally skip remapping Window.prototype because there is nothing
  // in it that needs to be remapped.
  env.lazyRemapProperties(blueRefs.EventTargetProto, blueRefs.EventTargetProtoOwnKeys);
  // We don't remap `blueRefs.WindowPropertiesProto` because it is "magical"
  // in that it provides access to elements by id.
  //
  // Once we get the iframe info ready, and all mapped, we can proceed to
  // detach the iframe only if `options.keepAlive` isn't true.
  if (keepAlive) {
    // @TODO: Temporary hack to preserve the document reference in Firefox.
    // https://bugzilla.mozilla.org/show_bug.cgi?id=543435
    const {
      document: redDocument
    } = redWindow;
    // Revoke the proxies of the redDocument and redWindow to prevent access.
    revoked.add(redDocument);
    revoked.add(redWindow);
    ReflectApply(DocumentProtoOpen, redDocument, []);
    ReflectApply(DocumentProtoClose, redDocument, []);
  } else {
    if (IS_OLD_CHROMIUM_BROWSER) {
      // For Chromium < v86 browsers we evaluate the `window` object to
      // kickstart the realm so that `window` persists when the iframe is
      // removed from the document.
      redIndirectEval('window');
    }
    ReflectApply(ElementProtoRemove, iframe, []);
  }
  return env;
}
function revokedProxyCallback(value) {
  return revoked.has(value);
}
export { createIframeVirtualEnvironment as default };
